import { useState, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { registerPrologLanguage } from '../prologLanguage';

const MIN_WIDTH          = 180;
const COLLAPSE_THRESHOLD = 90;
const DEFAULT_WIDTH      = 480;
const DEFAULT_CLAUSES_H  = 180;
const MIN_CLAUSES_H      = 40;
const DEFAULT_QUERY_H    = 22;
const MIN_QUERY_H        = 22;

const BASE_EDITOR_OPTIONS = {
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    wordWrap: 'off',
    lineNumbers: 'off',
    folding: false,
    renderLineHighlight: 'none',
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    scrollbar: { vertical: 'hidden', horizontal: 'hidden' },
    fontSize: 13,
    fontFamily: 'monospace',
    automaticLayout: true,
};

// ── Shared vertical-resize logic ─────────────────────────────────────────────
// Computes the ceiling from the sidebar's CSS max-height (calc(100% - 75px) on
// .code-sidebar relative to app-main), so the drag can never push the editor
// off-screen.  All three refs are stable (created once), so the handlers that
// call this function can safely declare [] as their dependency array.
function startVResize(e, wrapRef, panelBodyRef, setHeight, minH) {
    e.preventDefault();
    const wrap = wrapRef.current;
    const body = panelBodyRef.current;
    if (!wrap || !body) return;

    const startY      = e.clientY;
    const startHeight = wrap.offsetHeight;

    // Walk up: panelBody → code-sidebar → app-main
    const appMain     = body.parentElement?.parentElement;
    const appMainH    = appMain?.clientHeight ?? window.innerHeight;
    // .code-sidebar has max-height: calc(100% - 75px) where 100% = appMain height
    const sidebarMaxH = appMainH - 75;

    // Vertical padding + borders on the panel body
    const bodyCS  = window.getComputedStyle(body);
    const bodyPad = parseFloat(bodyCS.paddingTop)    + parseFloat(bodyCS.paddingBottom)
                  + parseFloat(bodyCS.borderTopWidth) + parseFloat(bodyCS.borderBottomWidth);

    // Everything in the panel body except this particular editor wrap
    const otherH = body.scrollHeight - wrap.offsetHeight;

    // Hard ceiling: leave 4 px breathing room
    const maxH = sidebarMaxH - bodyPad - otherH - 4;

    const onMove = (mv) => {
        const dy = mv.clientY - startY;
        setHeight(Math.max(minH, Math.min(startHeight + dy, maxH)));
    };
    const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup',   onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
}

export function CodePanel({ clauses, setClauses, query, setQuery, presets, clauseError, lightMode, getMaxWidth, onClausesMount }) {

    // ── State ────────────────────────────────────────────────────────────────
    const [width,         setWidth]         = useState(DEFAULT_WIDTH);
    const [collapsed,     setCollapsed]     = useState(false);
    const [clausesHeight, setClausesHeight] = useState(DEFAULT_CLAUSES_H);
    const [queryHeight,   setQueryHeight]   = useState(DEFAULT_QUERY_H);

    // ── Refs ─────────────────────────────────────────────────────────────────
    const prevWidthRef   = useRef(DEFAULT_WIDTH);
    const panelBodyRef   = useRef(null);
    const clausesWrapRef = useRef(null);
    const queryWrapRef   = useRef(null);

    // ── Horizontal drag ───────────────────────────────────────────────────────
    const handleHResize = useCallback((e) => {
        e.preventDefault();
        const startX     = e.clientX;
        const startWidth = collapsed ? 0 : width;
        let   didDrag    = false;

        // Minimum width = both header elements at their actual rendered sizes,
        // plus the gap between them (0.4rem ≈ 6px) and the body's L+R padding
        // (~10.4px each side ≈ 22px total).  We use offsetWidth — the real
        // painted width — not scrollWidth, so long option text never inflates it.
        const body   = panelBodyRef.current;
        const btn    = body?.querySelector('.code-toggle-btn');
        const sel    = body?.querySelector('.preset-select');
        const btnW   = btn?.offsetWidth ?? 18;
        const selW   = sel?.offsetWidth ?? 0;
        const minW   = btnW + (selW > 0 ? 6 + selW : 0) + 22;

        const onMove = (mv) => {
            didDrag = true;
            const raw     = startWidth + (mv.clientX - startX);
            const maxW    = getMaxWidth?.() ?? Infinity;
            const clamped = Math.min(raw, maxW);
            if (clamped < COLLAPSE_THRESHOLD) {
                setCollapsed(true);
            } else if (clamped < minW) {
                setCollapsed(false);
                setWidth(minW);
                prevWidthRef.current = minW;
            } else {
                setCollapsed(false);
                setWidth(clamped);
                prevWidthRef.current = clamped;
            }
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup',   onUp);
            if (!didDrag && collapsed) {
                setCollapsed(false);
                setWidth(prevWidthRef.current);
            }
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup',   onUp);
    }, [collapsed, width, getMaxWidth]);

    // ── Vertical drags ────────────────────────────────────────────────────────
    const handleClausesVResize = useCallback(
        (e) => startVResize(e, clausesWrapRef, panelBodyRef, setClausesHeight, MIN_CLAUSES_H),
        []
    );
    const handleQueryVResize = useCallback(
        (e) => startVResize(e, queryWrapRef, panelBodyRef, setQueryHeight, MIN_QUERY_H),
        []
    );

    // ── Collapse toggle ───────────────────────────────────────────────────────
    const handleCollapseClick = useCallback(() => {
        if (collapsed) { setCollapsed(false); setWidth(prevWidthRef.current); }
        else           { setCollapsed(true); }
    }, [collapsed]);

    // ── Body style ────────────────────────────────────────────────────────────
    const bodyStyle = collapsed
        ? { width: 0, minWidth: 0, padding: 0, overflow: 'hidden', border: 'none' }
        : { width };

    return (
        <div className={`code-sidebar${collapsed ? ' code-sidebar-collapsed' : ''}`}>

            <div className="code-panel-body" style={bodyStyle} ref={panelBodyRef}>

                {/* Header row: preset selector (left) + collapse button (right) */}
                <div className="code-panel-header-row">
                    {presets.length > 0 && (
                        <select
                            className="preset-select"
                            defaultValue=""
                            onChange={e => {
                                const preset = presets.find(p => p.name === e.target.value);
                                if (preset) { setClauses(preset.clauses); setQuery(preset.query); }
                                e.target.value = '';
                            }}
                        >
                            <option value="" disabled>Load preset…</option>
                            {presets.map(p => (
                                <option key={p.name} value={p.name}>{p.name}</option>
                            ))}
                        </select>
                    )}
                    <button
                        className="code-toggle-btn"
                        onClick={handleCollapseClick}
                        title="Collapse code panel"
                    >◀</button>
                </div>

                <div className="code-editor-section">
                    <label>Clauses</label>
                    {/* Container gives the height; handle sits inside at the bottom */}
                    <div className="editor-resize-container" style={{ height: clausesHeight }} ref={clausesWrapRef}>
                        <div className="monaco-wrap" style={{ height: '100%' }}>
                            <Editor
                                height="100%"
                                beforeMount={registerPrologLanguage}
                                theme={lightMode ? 'prolog-light' : 'prolog-dark'}
                                language="prolog"
                                value={clauses}
                                onChange={v => setClauses(v ?? '')}
                                options={BASE_EDITOR_OPTIONS}
                                onMount={onClausesMount}
                            />
                        </div>
                        <div className="editor-vresize-handle" onMouseDown={handleClausesVResize} />
                    </div>
                    {clauseError && <pre className="clause-error">{clauseError}</pre>}
                </div>

                <div className="code-editor-section">
                    <label>Query</label>
                    <div className="editor-resize-container" style={{ height: queryHeight }} ref={queryWrapRef}>
                        <div className="monaco-wrap" style={{ height: '100%' }}>
                            <Editor
                                height="100%"
                                beforeMount={registerPrologLanguage}
                                theme={lightMode ? 'prolog-light' : 'prolog-dark'}
                                language="prolog"
                                value={query}
                                onChange={v => setQuery(v ?? '')}
                                options={BASE_EDITOR_OPTIONS}
                            />
                        </div>
                        <div className="editor-vresize-handle" onMouseDown={handleQueryVResize} />
                    </div>
                </div>

            </div>

            {/* Horizontal drag handle */}
            <div
                className="code-resize-handle"
                onMouseDown={handleHResize}
                title="Drag to resize"
            />

            {/* Expand button — only visible when the panel is collapsed */}
            {collapsed && (
                <button
                    className="code-expand-btn"
                    onClick={handleCollapseClick}
                    title="Expand code panel"
                >▶</button>
            )}
        </div>
    );
}
